/**
 * Weights are stored as one number of ounces and entered as pounds plus
 * ounces: "35" oz, "1.3" lbs, or 1 lb and 10 oz all mean what they say.
 */

export const OZ_PER: Record<string, number> = { oz: 1, lbs: 16, kg: 35.27396195 };

/** A stored weight (value + unit) in ounces, or null. */
export function toOz(value: unknown, unit: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value * (OZ_PER[typeof unit === "string" ? unit : "oz"] ?? 1);
}

/** Pounds and ounces as typed ("" for empty) → total ounces, or null when both are empty. */
export function totalOz(lbs: string, oz: string): number | null {
  const l = lbs.trim() === "" ? null : Number(lbs);
  const o = oz.trim() === "" ? null : Number(oz);
  if (l === null && o === null) return null;
  if ((l !== null && (!Number.isFinite(l) || l < 0)) || (o !== null && (!Number.isFinite(o) || o < 0))) {
    throw new Error("Weights must be positive numbers");
  }
  return Math.round(((l ?? 0) * 16 + (o ?? 0)) * 10) / 10;
}

/** Total ounces → whole pounds and the ounces left over, for the two fields. */
export function splitOz(total: number | null): { lbs: string; oz: string } {
  if (total === null) return { lbs: "", oz: "" };
  const lbs = Math.floor(total / 16);
  const oz = Math.round((total - lbs * 16) * 10) / 10;
  return { lbs: lbs > 0 ? String(lbs) : "", oz: oz > 0 || lbs === 0 ? String(oz) : "" };
}

/** "4.2 oz", "1 lb 4.7 oz", "2 lbs". */
export function formatOz(total: number | null): string | null {
  if (total === null) return null;
  const { lbs, oz } = splitOz(total);
  const parts = [lbs ? `${lbs} ${lbs === "1" ? "lb" : "lbs"}` : "", oz ? `${oz} oz` : ""].filter(Boolean);
  return parts.join(" ") || "0 oz";
}
